import {
  BodyText,
  Button,
  Callout,
  DataTable,
  Skeleton,
  Tooltip,
} from "@/components/ui";
import { formatDate } from "@/lib/time";
import type { CellContext } from "@tanstack/react-table";
import { useState } from "react";
import { useOrderResultHistory } from "../../network/useOrderResultHistory";
import { DxReportViewer } from "../DxReportViewer/DxReportViewer";
import { ResultsDataGridHeader } from "./ResultsDataGridHeader";

interface ResultsDataGridProps {
  patientId: string;
  testCode: string;
  requestGroupId?: string;
}

type PivotedData = {
  id: string;
  specimen: string;
  result: string;
  date: string;
  dxReportPdfUrls: Set<string>;
  diagnosticReportId: string;
};

export const ResultsDataGrid = ({
  patientId,
  testCode,
  requestGroupId,
}: ResultsDataGridProps) => {
  const { data, isLoading, isError, refetch } = useOrderResultHistory({
    patientId,
    testCode,
  });

  const [selectedDiagnosticReportId, setSelectedDiagnosticReportId] = useState<
    string | null
  >(null);

  const launchDxReportViewer = (diagnosticReportId: string) => {
    setSelectedDiagnosticReportId(diagnosticReportId);
  };

  const flattenedResults = (): PivotedData[] => {
    const pivotedResults: PivotedData[] = [];

    for (const item of data?.orderResultHistory ?? []) {
      for (const report of item.reportResults) {
        const date =
          report.diagnosticReport.issued ??
          report.diagnosticReport.effectiveDateTime;

        const dxReportPdfUrls = new Set(
          report.diagnosticReport.presentedForm
            ?.map((attachment) => attachment.url)
            .filter(Boolean),
        );

        const diagnosticReportId = report.diagnosticReport.id;
        const formattedDate = formatDate(date);

        pivotedResults.push(
          ...report.observations.map((observation) => {
            return {
              id: `${formattedDate}-${observation.code.coding?.[0]?.code}`,
              specimen:
                observation.code.coding?.[0]?.display || observation.code.text,
              result: observation.normalizedObservationDisplay,
              date: formattedDate,
              dxReportPdfUrls,
              diagnosticReportId,
            } as PivotedData;
          }),
        );
      }
    }
    return pivotedResults;
  };

  const flattenedData = flattenedResults();
  const dates = [...new Set(flattenedData.map((item) => item.date))];

  const resultsBySpecimenAndDate: Record<string, Record<string, string>> = {};
  const dxReportPdfsByDate: Record<string, Set<string>> = {};
  const diagnosticReportIdsByDate: Record<string, Set<string>> = {};

  for (const item of flattenedData) {
    if (!resultsBySpecimenAndDate[item.specimen]) {
      resultsBySpecimenAndDate[item.specimen] = {
        specimen: item.specimen,
      };
    }
    resultsBySpecimenAndDate[item.specimen][item.date] = item.result;

    for (const dxReportPdfUrl of item.dxReportPdfUrls) {
      if (!dxReportPdfsByDate[item.date]) {
        dxReportPdfsByDate[item.date] = new Set();
      }
      dxReportPdfsByDate[item.date].add(dxReportPdfUrl);
    }

    if (!diagnosticReportIdsByDate[item.date]) {
      diagnosticReportIdsByDate[item.date] = new Set();
    }
    diagnosticReportIdsByDate[item.date].add(item.diagnosticReportId);
  }

  const columns = [
    {
      id: "specimen",
      accessorKey: "specimen",
      header: "",
      meta: {
        className: "overflow-hidden text-ellipsis max-w-24",
      },
      cell: (info: CellContext<Record<string, string>, string>) => (
        <Tooltip tooltipContentText={info.getValue()}>
          <BodyText size="sm" textColor="primary" truncate>
            {info.getValue()}
          </BodyText>
        </Tooltip>
      ),
    },
    ...dates.map((date) => {
      return {
        id: date,
        accessorKey: date,
        meta: {},
        header: () => (
          <ResultsDataGridHeader
            date={date}
            dxReportPdfUrls={dxReportPdfsByDate[date]}
            diagnosticReportIds={diagnosticReportIdsByDate[date]}
            launchDxReportViewer={launchDxReportViewer}
          />
        ),
        cell: (info: CellContext<Record<string, string>, string>) => {
          const value = info.getValue();
          if (!Number.isNaN(Number.parseFloat(value))) {
            return (
              <BodyText size="sm" textColor="primary">
                {Number.isInteger(Number(value)) ? `${value}.0` : `${value}`}
              </BodyText>
            );
          }
          return (
            <BodyText size="sm" textColor="primary">
              {value ?? "--"}
            </BodyText>
          );
        },
      };
    }),
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Callout
        variant="danger"
        title="Error loading results"
        description="If the problem persists, please contact support."
      >
        <Button
          size="sm"
          variant="link"
          onClick={() => refetch()}
          className="ml-1"
        >
          Retry
        </Button>
      </Callout>
    );
  }

  return (
    <>
      <div className="overflow-x-scroll">
        <DataTable
          variant="grid"
          columns={columns}
          data={Object.values(resultsBySpecimenAndDate)}
        />
      </div>
      {selectedDiagnosticReportId && requestGroupId && (
        <DxReportViewer
          key={selectedDiagnosticReportId}
          diagnosticReportId={selectedDiagnosticReportId}
          requestGroupId={requestGroupId}
          onClose={() => setSelectedDiagnosticReportId(null)}
        />
      )}
    </>
  );
};
